import React from 'react';
import { Link } from 'react-router-dom';
import ImageCompareSlider from '../components/ImageCompareSlider';
import { KITCHEN_IMAGES, BATHROOM_IMAGES, CARPENTRY_IMAGES, BASEMENT_IMAGES, COMPARES } from '../content';

const BASEMENT_POSTER = 'https://static.prod-images.emergentagent.com/jobs/d2574768-c6b6-47d1-b3f5-5365e206636a/images/4ce44d32712fe8b93c6522cda16ce43225363cc3c6d7cd65df845d9eb8e26946.png';
const BATHROOM_POSTER = 'https://static.prod-images.emergentagent.com/jobs/d2574768-c6b6-47d1-b3f5-5365e206636a/images/947a5269079df45d808de372f76e7ee2a3ea1a04085eae597e6c0d5cb865f40c.png';
const CARPENTRY_POSTER = 'https://static.prod-images.emergentagent.com/jobs/d2574768-c6b6-47d1-b3f5-5365e206636a/images/14ebda54782e9ebee13b2f4673576891db49487ff7b7e18ba94a94a7dcc61683.png';

// Per-category data lives here (not passed across files as props) so the
// build's metadata tracer never has to follow an imported array prop.
const DATA = {
  kitchen: { images: KITCHEN_IMAGES, beforeAfter: COMPARES[1] },
  bathroom: { images: BATHROOM_IMAGES, beforeAfter: COMPARES[0], heroVideo: '/hero-bathroom.mp4?v=fix', heroPoster: BATHROOM_POSTER, heroOverline: 'A Daily Sanctuary', heroTitle: 'Step into your private spa.' },
  carpentry: { images: CARPENTRY_IMAGES, beforeAfter: null, heroVideo: '/hero-carpentry.mp4?v=bright', heroPoster: CARPENTRY_POSTER, heroOverline: 'Made By Hand', heroTitle: 'Woodwork that becomes the room.' },
  basement: { images: BASEMENT_IMAGES, beforeAfter: null, heroVideo: '/hero-basement.mp4?v=fix', heroPoster: BASEMENT_POSTER, heroOverline: 'Spaces That Live Below', heroTitle: "Step into the space you didn't know you had." },
};

const DUST = Array.from({ length: 14 }, (_, i) => ({
  left: (i * 67 + 9) % 100,
  top: (i * 41 + 13) % 100,
  dur: 9 + (i % 6) * 2,
  delay: (i % 5) * 1.4,
  scale: 0.5 + (i % 4) * 0.35,
}));

// Reusable category-page shell. The three service pages
// (Kitchens / Bathrooms / Carpentry) each call this with their own
// category + copy so there is one layout to maintain.
export default function ServicePage({ category, eyebrow, title, intro, description }) {
  const data = DATA[category] || DATA.kitchen;
  const images = data.images;
  const beforeAfter = data.beforeAfter;
  const heroImage = images[0];

  return (
    <>
      <section className="pt-40 pb-20 border-b border-white/5" data-testid={`service-hero-${category}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="gl-overline mb-6 gl-reveal"><span className="gl-hairline" />{eyebrow}</div>
          <h1 className="gl-serif text-white text-5xl sm:text-7xl lg:text-[8rem] leading-[0.92] gl-reveal" style={{ transitionDelay: '0.05s' }}>{title}</h1>
          <div className="mt-10 grid md:grid-cols-2 gap-12 max-w-5xl">
            <p className="gl-serif text-2xl sm:text-3xl leading-snug text-[color:var(--gl-fg)] gl-reveal" style={{ transitionDelay: '0.12s' }}>{intro}</p>
            <p className="text-base text-[color:var(--gl-muted)] leading-relaxed gl-reveal" style={{ transitionDelay: '0.18s' }}>{description}</p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/5">
        {data.heroVideo ? (
          <div className="relative h-[80vh] min-h-[520px] overflow-hidden" data-testid="basement-video-hero">
            <video className="gl-hero-video" poster={data.heroPoster} autoPlay muted loop playsInline preload="auto" aria-hidden="true">
              <source src={data.heroVideo} type="video/mp4" />
            </video>
            <div className="gl-hero-particles" aria-hidden="true">
              {DUST.map((d, i) => (
                <span key={i} className="gl-dust" style={{ left: `${d.left}%`, top: `${d.top}%`, animationDuration: `${d.dur}s`, animationDelay: `${d.delay}s`, transform: `scale(${d.scale})` }} />
              ))}
            </div>
            <div className="gl-video-veil" />
            <div className="relative z-10 h-full flex items-end max-w-7xl mx-auto px-6 lg:px-12 pb-16">
              <div className="gl-reveal">
                <div className="gl-overline mb-3">{data.heroOverline}</div>
                <h2 className="gl-serif text-white text-4xl sm:text-6xl lg:text-7xl leading-none max-w-3xl">{data.heroTitle}</h2>
              </div>
            </div>
          </div>
        ) : (
          <div className="aspect-[21/9] w-full overflow-hidden">
            <img src={heroImage.src} alt={heroImage.title} className="w-full h-full object-cover gl-reveal" />
          </div>
        )}
      </section>

      {beforeAfter && (
        <section className="py-24 border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12 gl-reveal">
              <div className="gl-overline mb-3">The Reveal</div>
              <h2 className="gl-serif text-white text-4xl sm:text-5xl">Drag to see the difference.</h2>
            </div>
            <div className="gl-reveal aspect-[16/10] w-full max-w-5xl mx-auto overflow-hidden">
              <ImageCompareSlider imageA={beforeAfter.before} imageB={beforeAfter.after} labelA="BEFORE" labelB="AFTER" />
            </div>
          </div>
        </section>
      )}

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12 gl-reveal">
            <div>
              <div className="gl-overline mb-3">Selected Work</div>
              <h2 className="gl-serif text-white text-4xl sm:text-5xl">Recent projects.</h2>
            </div>
            <Link to="/contact" className="hidden md:inline-flex text-xs uppercase tracking-[0.2em] text-white border-b border-[color:var(--gl-primary)] pb-1">Inquire →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((img, i) => (
              <figure key={img.title} className="gl-tile aspect-[4/5] gl-reveal" style={{ transitionDelay: `${(i % 3) * 0.08}s` }}>
                <div className="absolute inset-0">
                  <img src={img.src} alt="Greenline project" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/5 text-center">
        <div className="max-w-3xl mx-auto px-6 gl-reveal">
          <div className="gl-overline mb-4">Ready to Begin?</div>
          <h2 className="gl-serif text-white text-4xl sm:text-6xl leading-tight mb-8">Tell us about your space.</h2>
          <Link to="/contact" className="gl-btn-primary">Get a Free Estimate →</Link>
        </div>
      </section>
    </>
  );
}
